import { useState, useEffect } from "react"
import AnalyticsClient from "~/util/analytics-client"
import { useAuth } from "@ncs/ncs-api"

/**
 * Custom React hook to retrieve the current variant of a feature flag for the authenticated user.
 * Please refer to the Notion document : https://www.notion.so/A-B-Testing-2bc05b3dd23880f5a29ff08ce734e9ee
 * @param featureFlagName - The name of the feature flag to fetch the variant for.
 * @returns The variant string (e.g., "a", "b", or another variant key) for the authenticated user
 *
 * @throws {Error} If an error occurs during the fetch operation.
 *
 * @example
 * const variant = useExperiment("add_to_cart_experiment");
 * if (variant === "a") {
 *   return <div>This is A</div>;
 * } else if (variant === "b") {
 *   return <div>This is B</div>;
 * } else {
 *   return null;
 * }
 */
export const useExperiment = (featureFlagName: string) => {
	const [variant, setVariant] = useState<string | undefined>()
	const auth = useAuth()

	useEffect(() => {
		const fetchVariants = async () => {
			if (auth?.user?.id) {
				const experiment = AnalyticsClient.getExperimentInstance()
				if (experiment) {
					void (await experiment?.fetch({ user_id: auth?.user?.id }))
					setVariant(experiment?.variant(featureFlagName).value)
				}
			}
		}

		void fetchVariants()
	}, [auth])

	return variant
}
